import type { ThreadRuntimeMaterialization } from "@/lib/thread-sync-runtime-materialization"

export type ParsedSyncHistoryTokenDebug = {
  raw: string
  version: string | null
  historyContinuity: "contiguous" | "broken" | null
  headCursor: number | null
  lineageField: "journal_epoch" | "browser_history_epoch" | "unknown"
  lineageValue: string | null
  baselineFingerprint: string | null
}

export type SyncReadyResetReason =
  | "history_continuity_broken"
  | "history_token_mismatch"
  | "cursor_regression"

export type SyncReadyDebugSnapshot = {
  at: number
  resetTriggered: boolean
  reasonCodes: SyncReadyResetReason[]
  historyContinuity: "contiguous" | "broken"
  replicationContinuity: "contiguous" | "broken"
  hasHistoryContinuityBroken: boolean
  hasHistoryTokenMismatch: boolean
  hasCursorRegression: boolean
  hasActiveRuntimeSnapshot: boolean
  hasCurrentConnectionAuthoritativeRuntime: boolean
  acceptedCursor: number
  tailCursor: number
  activeLastEventId: number | null
  runtimeMaterialization: ThreadRuntimeMaterialization
  previousHistoryToken: string | null
  nextHistoryToken: string | null
  previousParsedHistoryToken: ParsedSyncHistoryTokenDebug | null
  nextParsedHistoryToken: ParsedSyncHistoryTokenDebug | null
}

// Tokens always contain at least: version, continuity, head cursor, lineage
// component, and one baseline fingerprint segment. Shorter payloads are treated
// as opaque strings so malformed tokens still remain visible in diagnostics.
const SYNC_HISTORY_TOKEN_MIN_PARTS = 5
// Baseline fingerprints contain embedded `:` characters, so every segment after
// the lineage component belongs to the fingerprint payload.
const SYNC_HISTORY_TOKEN_FINGERPRINT_PART_START_INDEX = 4

function normalizeIntegerString(args: { value: string | null | undefined }): number | null {
  if (typeof args.value !== "string") {
    return null
  }

  const parsed = Number.parseInt(args.value, 10)
  if (!Number.isFinite(parsed)) {
    return null
  }

  return Math.max(0, Math.floor(parsed))
}

export function parseSyncHistoryTokenDebug(args: {
  token: string | null
}): ParsedSyncHistoryTokenDebug | null {
  if (typeof args.token !== "string") {
    return null
  }

  const trimmedToken = args.token.trim()
  if (trimmedToken.length === 0) {
    return null
  }

  const parts = trimmedToken.split(":")
  if (parts.length < SYNC_HISTORY_TOKEN_MIN_PARTS) {
    return {
      raw: trimmedToken,
      version: null,
      historyContinuity: null,
      headCursor: null,
      lineageField: "unknown",
      lineageValue: null,
      baselineFingerprint: null,
    }
  }

  const version = parts[0] ?? null
  const historyContinuity = parts[1] === "contiguous" || parts[1] === "broken"
    ? parts[1]
    : null
  const lineageField = version === "v2"
    ? "journal_epoch"
    : version === "v3" || version === "v4"
      ? "browser_history_epoch"
      : "unknown"

  return {
    raw: trimmedToken,
    version,
    historyContinuity,
    headCursor: normalizeIntegerString({ value: parts[2] ?? null }),
    lineageField,
    lineageValue: parts[3] ?? null,
    baselineFingerprint: parts.slice(SYNC_HISTORY_TOKEN_FINGERPRINT_PART_START_INDEX).join(":"),
  }
}

function buildSyncReadyReasonCodes(args: {
  resetTriggered: boolean
  hasHistoryContinuityBroken: boolean
  hasHistoryTokenMismatch: boolean
  hasCursorRegression: boolean
}): SyncReadyResetReason[] {
  if (args.resetTriggered === false) {
    return []
  }

  const reasons: SyncReadyResetReason[] = []

  if (args.hasHistoryContinuityBroken) {
    reasons.push("history_continuity_broken")
  }
  if (args.hasHistoryTokenMismatch) {
    reasons.push("history_token_mismatch")
  }
  if (args.hasCursorRegression) {
    reasons.push("cursor_regression")
  }

  return reasons
}

export function buildSyncReadyDebugSnapshot(args: {
  nowMs: number
  resetTriggered: boolean
  historyContinuity: "contiguous" | "broken"
  replicationContinuity: "contiguous" | "broken"
  hasHistoryContinuityBroken: boolean
  hasHistoryTokenMismatch: boolean
  hasCursorRegression: boolean
  hasActiveRuntimeSnapshot: boolean
  hasCurrentConnectionAuthoritativeRuntime: boolean
  acceptedCursor: number
  tailCursor: number
  activeLastEventId: number | null
  runtimeMaterialization: ThreadRuntimeMaterialization
  previousHistoryToken: string | null
  nextHistoryToken: string | null
}): SyncReadyDebugSnapshot {
  return {
    at: args.nowMs,
    resetTriggered: args.resetTriggered,
    reasonCodes: buildSyncReadyReasonCodes({
      resetTriggered: args.resetTriggered,
      hasHistoryContinuityBroken: args.hasHistoryContinuityBroken,
      hasHistoryTokenMismatch: args.hasHistoryTokenMismatch,
      hasCursorRegression: args.hasCursorRegression,
    }),
    historyContinuity: args.historyContinuity,
    replicationContinuity: args.replicationContinuity,
    hasHistoryContinuityBroken: args.hasHistoryContinuityBroken,
    hasHistoryTokenMismatch: args.hasHistoryTokenMismatch,
    hasCursorRegression: args.hasCursorRegression,
    hasActiveRuntimeSnapshot: args.hasActiveRuntimeSnapshot,
    hasCurrentConnectionAuthoritativeRuntime: args.hasCurrentConnectionAuthoritativeRuntime,
    acceptedCursor: args.acceptedCursor,
    tailCursor: args.tailCursor,
    activeLastEventId: args.activeLastEventId,
    runtimeMaterialization: args.runtimeMaterialization,
    previousHistoryToken: args.previousHistoryToken,
    nextHistoryToken: args.nextHistoryToken,
    previousParsedHistoryToken: parseSyncHistoryTokenDebug({ token: args.previousHistoryToken }),
    nextParsedHistoryToken: parseSyncHistoryTokenDebug({ token: args.nextHistoryToken }),
  }
}
